/**
 * Evidence room: an item's life from collection to release or destruction. Every step is recorded; an item
 * released or destroyed is closed for good.
 */

export type EvidenceStatus = 'pending' | 'in_custody' | 'checked_out' | 'released' | 'destroyed';
export type EvidenceAction = 'check_in' | 'move' | 'check_out' | 'return' | 'release' | 'destroy' | 'note';

export const EVIDENCE_STATUS_LABEL: Record<EvidenceStatus, string> = {
  pending: 'Waiting to be checked in',
  in_custody: 'In the evidence room',
  checked_out: 'Checked out',
  released: 'Released',
  destroyed: 'Destroyed',
};

export const EVIDENCE_ACTION_LABEL: Record<EvidenceAction | 'collected', string> = {
  collected: 'Collected',
  check_in: 'Checked in',
  move: 'Moved',
  check_out: 'Checked out',
  return: 'Returned',
  release: 'Released to owner',
  destroy: 'Destroyed',
  note: 'Note',
};

export const CHECKOUT_PURPOSES = ['Lab (SLED)', 'Court', 'Detective / investigator', 'Prosecutor (solicitor)', 'Photograph / examine', 'Other'];
export const DESTROY_METHODS = ['Incinerated', 'Shredded', 'Crushed', 'Poured out / drained', 'Firearm destroyed by vendor', 'Other'];

/** Which actions make sense from a status, and where they lead. */
export const EVIDENCE_FLOW: Record<EvidenceStatus, Partial<Record<EvidenceAction, EvidenceStatus>>> = {
  pending: { check_in: 'in_custody', note: 'pending' },
  in_custody: { move: 'in_custody', check_out: 'checked_out', release: 'released', destroy: 'destroyed', note: 'in_custody' },
  checked_out: { return: 'in_custody', note: 'checked_out' },
  released: { note: 'released' },
  destroyed: { note: 'destroyed' },
};

export interface EvidenceStep {
  action: EvidenceAction;
  location?: string | null;
  person?: string | null;
  purpose?: string | null;
  dueBack?: string | null;
  note?: string | null;
  method?: string | null;
  witness?: string | null;
}

/** What's missing for this step, in plain words (empty = OK). */
export function checkEvidenceStep(status: EvidenceStatus, s: EvidenceStep): string[] {
  const next = EVIDENCE_FLOW[status][s.action];
  if (!next) return [`An item that is “${EVIDENCE_STATUS_LABEL[status].toLowerCase()}” can’t be ${EVIDENCE_ACTION_LABEL[s.action].toLowerCase()}.`];
  const out: string[] = [];
  const blank = (v: string | null | undefined) => !v || !v.trim();
  if ((s.action === 'check_in' || s.action === 'move' || s.action === 'return') && blank(s.location)) out.push('Where is it going? (room, shelf, bin, locker)');
  if (s.action === 'check_out') {
    if (blank(s.person)) out.push('Who is taking it?');
    if (blank(s.purpose)) out.push('What for (lab, court…)?');
  }
  if (s.action === 'release' && blank(s.person)) out.push('Who was it released to?');
  if (s.action === 'destroy') {
    if (blank(s.method)) out.push('How was it destroyed?');
    if (blank(s.witness)) out.push('Who witnessed the destruction?');
    if (blank(s.note)) out.push('Who authorized it (court order, solicitor, supervisor)?');
  }
  if (s.action === 'note' && blank(s.note)) out.push('Write the note.');
  return out;
}
